import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Modal, Button, Input, Spin } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { useDeleteChannelMutation } from "../../../redux/channels/channelAPI";
import {
  updateOpenModalDeleteChannel,
  updateOpenModalEditChannel,
} from "../../../redux/channels/channelSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { styleDeleteButton } from "../../../utils/utils";
import { uuidToString } from "../../../utils/uuid";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
// INTERFACES
import { RootState } from "../../../app/store";

function ChannelsDeleteModal() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [disableButtonConfirmDelete, setDisableButtonConfirmDelete] = useState(true);
  const [confirmChannelName, setConfirmChannelName] = useState("");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { channel, openModalDeleteChannel } = useSelector((state: RootState) => state.channel);
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerDeleteChannel,
    { isLoading, isSuccess, error, isError, reset: resetDeleteChannel },
  ] = useDeleteChannelMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onClickClose = () => {
    dispatch(updateOpenModalDeleteChannel(false));
  };

  const handleDeleteItem = async () => {
    const token = await GETJwtToken();
    triggerDeleteChannel({ id: uuidToString(channel.id), token });
  };

  const handleSubmitDeleteItem = async (event: any) => {
    if (event.key === "Enter" && !disableButtonConfirmDelete) {
      const token = await GETJwtToken();
      triggerDeleteChannel({ id: uuidToString(channel.id), token });
    }
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    if (openModalDeleteChannel) {
      setDisableButtonConfirmDelete(true);
      setConfirmChannelName("");
      resetDeleteChannel();
    }
  }, [openModalDeleteChannel]);

  useEffect(() => {
    setDisableButtonConfirmDelete(true);
    if (
      confirmChannelName &&
      channel &&
      channel.name &&
      confirmChannelName.toLowerCase() === String(channel.name).toLowerCase()
    ) {
      setDisableButtonConfirmDelete(false);
    } else {
      setDisableButtonConfirmDelete(true);
    }
  }, [confirmChannelName]);

  useEffect(() => {
    dispatch(updateOpenModalEditChannel(false));
  }, [isSuccess, isError]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Modal
      onOk={onClickClose}
      closable={false}
      onCancel={onClickClose}
      open={openModalDeleteChannel}
      footer={null}
    >
      <Spin spinning={isLoading}>
        <div>
          <div className="text-center">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="generalStyles__warning generalStyles__deleteItemIcon"
            />
            <p className="mt-3 drawer__text">
              {t("organizationManagement.deleteDescription.text1")}
              <b className="generalStyles__warning">
                {t("organizationManagement.deleteDescription.text2")}{" "}
              </b>
              {t("edgeManagement.servers.theChannel")}{" "}
              <b className="generalStyles__warning">
                {channel && channel.name ? channel.name : ""}{" "}
              </b>
              {t("edgeManagement.servers.confirmChannelName")}
            </p>
            <Input
              placeholder={t("general.name")}
              onChange={(e) => {
                setConfirmChannelName(e.target.value);
              }}
              value={confirmChannelName}
              onKeyDown={handleSubmitDeleteItem}
              status="warning"
            />
          </div>
          <div className="generalStyles__flexEnd mt-4">
            <div>
              <Button
                htmlType="submit"
                className={styleDeleteButton(disableButtonConfirmDelete, theme)}
                onClick={handleDeleteItem}
                disabled={disableButtonConfirmDelete}
              >
                {t("organizationManagement.delete")}
              </Button>
              <Button onClick={onClickClose} className="buttonStyle__4">
                {t("organizationManagement.close")}
              </Button>
            </div>
          </div>
        </div>
      </Spin>
      {/** ---------------------------------------------------- */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isError}
        isSuccess={isSuccess}
        requestType="DELETE"
        error={error}
        name="Channel"
      />
    </Modal>
  );
}

export default ChannelsDeleteModal;
